import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Video, Square, Play, Pause, X, Loader2, ChevronDown } from 'lucide-react';

const MediaCapture = ({ onMediaCaptured, onClose, isOpen, initialMode = 'video' }) => {
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [stream, setStream] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actualMimeType, setActualMimeType] = useState(null);

  // Microphone selection state
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState(() => {
    // Load saved preference from localStorage
    return localStorage.getItem('preferredMicrophoneId') || '';
  });

  // Camera selection state
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(() => {
    return localStorage.getItem('preferredCameraId') || '';
  });

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  // Enumerate available devices (microphones and cameras)
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      setAvailableMicrophones(microphones);

      const cameras = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(cameras);

      // If no microphone is selected, or the selected one isn't available, use default
      if (!selectedMicrophoneId || !microphones.find(m => m.deviceId === selectedMicrophoneId)) {
        const defaultMic = microphones.find(m => m.deviceId === 'default') || microphones[0];
        if (defaultMic) {
          setSelectedMicrophoneId(defaultMic.deviceId);
        }
      }

      // If no camera is selected, or the selected one isn't available, use default
      if (!selectedCameraId || !cameras.find(c => c.deviceId === selectedCameraId)) {
        const defaultCam = cameras.find(c => c.deviceId === 'default') || cameras[0];
        if (defaultCam) {
          setSelectedCameraId(defaultCam.deviceId);
        }
      }
    } catch (err) {
      console.error('Error enumerating devices:', err);
    }
  }, [selectedMicrophoneId, selectedCameraId]);

  // Enumerate microphones when modal opens
  useEffect(() => {
    if (isOpen) {
      enumerateDevices();
    }
  }, [isOpen, enumerateDevices]);

  // Update mode when initialMode changes and modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentMode(initialMode);
    }
  }, [isOpen, initialMode]);

  // Handle microphone selection change
  const handleMicrophoneChange = async (deviceId) => {
    setSelectedMicrophoneId(deviceId);
    localStorage.setItem('preferredMicrophoneId', deviceId);

    // Re-setup media with the new microphone
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    // setupMedia will be called by the useEffect that watches for stream changes
  };

  // Handle camera selection change
  const handleCameraChange = async (deviceId) => {
    setSelectedCameraId(deviceId);
    localStorage.setItem('preferredCameraId', deviceId);

    // Re-setup media with the new camera
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  // Cleanup function
  const cleanup = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  }, [stream]);

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Additional cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Ensure all media streams are released when modal closes
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
        });
        setStream(null);
      }
      
      // Reset recording state
      setIsRecording(false);
      setRecordedChunks([]);
      setRecordingTime(0);
      setIsPlaying(false);
      
      // Clear any timers
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  }, [isOpen, stream]);

  // Initialize media stream
  const setupMedia = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build audio constraints with selected microphone
      const audioConstraints = {
        echoCancellation: false,  // Disable - can cause issues
        noiseSuppression: false,  // Disable - can filter out too much
        autoGainControl: true,    // Help with quiet microphones
      };

      // Add deviceId if a specific microphone is selected
      if (selectedMicrophoneId && selectedMicrophoneId !== 'default') {
        audioConstraints.deviceId = { exact: selectedMicrophoneId };
      }

      // Build video constraints with selected camera
      let videoConstraints = false;
      if (currentMode === 'video') {
        videoConstraints = {
          width: { ideal: 500 },
          height: { ideal: 280 }
        };
        if (selectedCameraId && selectedCameraId !== 'default') {
          videoConstraints.deviceId = { exact: selectedCameraId };
        }
      }

      const constraints = {
        video: videoConstraints,
        audio: audioConstraints
      };

      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (constraintErr) {
        // If constraints fail (e.g., device not available), try with simpler/default constraints
        if (constraintErr.name === 'OverconstrainedError' || constraintErr.name === 'NotSupportedError') {
          const simpleConstraints = {
            video: currentMode === 'video' ? true : false,
            audio: true
          };
          mediaStream = await navigator.mediaDevices.getUserMedia(simpleConstraints);
          // Update the list of available microphones
          enumerateDevices();
        } else if (constraintErr.name === 'NotReadableError' && currentMode === 'video') {
          // Camera hardware unavailable — fall back to audio-only
          console.warn('Camera not available, falling back to audio-only mode');
          mediaStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: audioConstraints });
          setCurrentMode('audio');
          enumerateDevices();
        } else {
          throw constraintErr;
        }
      }

      setStream(mediaStream);

      // Debug: Log stream info and show which microphone is being used
      const audioTrack = mediaStream.getAudioTracks()[0];
      if (audioTrack) {
      }

      // List all available audio input devices
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        audioInputs.forEach((device, i) => {
          const isCurrentDevice = audioTrack && device.label === audioTrack.label;
        });
      } catch (e) {
      }

      if (currentMode === 'video' && videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Error accessing media devices:', err);

      // Provide more specific error messages
      let errorMessage = 'Unable to access camera/microphone. ';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera/microphone access in your browser settings. In Firefox, click the camera icon in the address bar.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera or microphone found. Please connect a device and try again.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += 'Your camera or microphone is being used by another application. Please close other apps and try again.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage += 'Camera settings not supported by your device.';
      } else if (err.name === 'SecurityError') {
        errorMessage += 'Media access is blocked. Make sure you are using HTTPS or localhost.';
      } else {
        errorMessage += `Error: ${err.message || err.name || 'Unknown error'}`;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentMode, selectedMicrophoneId, selectedCameraId, enumerateDevices]);

  // Setup media when mode changes
  useEffect(() => {
    if (isOpen) {
      setupMedia();
    }
  }, [isOpen, currentMode, setupMedia]);

  // Switch between video and audio modes
  const switchMode = async (mode) => {
    if (mode === currentMode) return;
    
    setCurrentMode(mode);
    setRecordedChunks([]);
    setRecordingTime(0);
    setIsRecording(false);
    setIsPlaying(false);
    
    // Stop current stream and setup new one
    cleanup();
    await setupMedia();
  };

  // Get supported MIME type for recording
  const getSupportedMimeType = useCallback((mode) => {
    const videoTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4'
    ];

    const audioTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/mpeg'
    ];

    const types = mode === 'video' ? videoTypes : audioTypes;

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    console.warn('No supported MIME type found, using default');
    return undefined; // Let browser choose default
  }, []);

  // Start recording
  const startRecording = useCallback(() => {
    if (!stream) {
      console.error('No stream available for recording');
      return;
    }

    // Debug: Check stream tracks
    const audioTracks = stream.getAudioTracks();
    const videoTracks = stream.getVideoTracks();

    if (audioTracks.length === 0) {
      setError('No audio track found. Please check your microphone.');
      return;
    }

    // Ensure audio tracks are enabled
    audioTracks.forEach(track => {
      if (!track.enabled) {
        track.enabled = true;
      }
    });

    try {
      const mimeType = getSupportedMimeType(currentMode);
      const options = mimeType ? { mimeType } : {};

      const recorder = new MediaRecorder(stream, options);

      // Store the stream reference on the recorder to avoid closure issues
      recorder._streamRef = stream;

      // Store the actual MIME type being used
      setActualMimeType(recorder.mimeType || mimeType || (currentMode === 'video' ? 'video/webm' : 'audio/webm'));
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        } else {
          console.warn('Received empty data chunk');
        }
      };

      recorder.onstop = () => {
        const totalSize = chunks.reduce((acc, c) => acc + c.size, 0);

        if (totalSize < 5000) {
          console.warn('WARNING: Recording is very small, audio may not have been captured properly');
        }

        setRecordedChunks([...chunks]); // Create new array to ensure state update
        setIsRecording(false);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }

        // Use the stream reference stored on recorder to avoid closure issues
        const streamToStop = recorder._streamRef;
        if (streamToStop) {
          streamToStop.getTracks().forEach(track => {
            track.stop();
          });
          setStream(null);
        }
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError(`Recording error: ${event.error?.message || 'Unknown error'}`);
      };

      // Start recording with timeslice to collect data every second
      // This ensures data is captured incrementally, not just at the end
      recorder.start(1000);
      setMediaRecorder(recorder);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordedChunks([]);

      // Start timer
      const startTime = Date.now();
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording. Please try again.');
    }
  }, [stream, currentMode]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorder && isRecording) {
      // Request any pending data before stopping
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.requestData();
      }

      // Stop the recorder - this triggers onstop which collects chunks
      mediaRecorder.stop();
      setIsRecording(false);

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      // Reset video preview to show the recorded content
      if (currentMode === 'video' && videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // NOTE: Don't stop stream tracks here - let onstop handler finish collecting data first
      // Stream will be released when modal closes or when saving
    }
  }, [mediaRecorder, isRecording, currentMode]);

  // Play recorded media
  const playRecording = useCallback(() => {
    if (recordedChunks.length === 0) return;

    try {
      const mimeType = actualMimeType || (currentMode === 'video' ? 'video/webm' : 'audio/webm');
      const blob = new Blob(recordedChunks, { type: mimeType });
      const url = URL.createObjectURL(blob);

      if (currentMode === 'video') {
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = url;
          videoRef.current.muted = false; // Unmute for playback (was muted for live preview)
          videoRef.current.controls = true;
          videoRef.current.play();
          setIsPlaying(true);

          videoRef.current.onended = () => setIsPlaying(false);
        }
      } else {
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.volume = 1.0;
          audioRef.current.muted = false;

          // Handle play promise (browser autoplay policies)
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
              })
              .catch(err => {
                console.error('Audio playback failed:', err);
                setError('Could not play audio. Please click play again.');
              });
          } else {
            setIsPlaying(true);
          }

          audioRef.current.onended = () => setIsPlaying(false);
        }
      }
    } catch (err) {
      console.error('Error playing recording:', err);
      setError('Failed to play recording. Please try again.');
    }
  }, [recordedChunks, currentMode, actualMimeType]);

  // Save captured media
  const saveMedia = useCallback(() => {
    if (recordedChunks.length === 0) return;

    try {
      const mimeType = actualMimeType || (currentMode === 'video' ? 'video/webm' : 'audio/webm');
      const blob = new Blob(recordedChunks, { type: mimeType });

      // Determine file extension from MIME type
      const getExtension = (mime) => {
        if (mime.includes('webm')) return 'webm';
        if (mime.includes('mp4')) return 'mp4';
        if (mime.includes('ogg')) return 'ogg';
        if (mime.includes('mpeg')) return 'mp3';
        return 'webm';
      };

      const extension = getExtension(mimeType);
      const file = new File([blob], `captured_${currentMode}_${Date.now()}.${extension}`, {
        type: mimeType
      });

      // Create media object with metadata
      const mediaData = {
        file,
        type: currentMode,
        duration: recordingTime,
        preview: URL.createObjectURL(blob),
        timestamp: new Date().toISOString()
      };

      // Release the media stream before closing
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
        });
        setStream(null);
      }

      onMediaCaptured(mediaData);
      onClose();
    } catch (err) {
      console.error('Error saving media:', err);
      setError('Failed to save media. Please try again.');
    }
  }, [recordedChunks, currentMode, recordingTime, onMediaCaptured, onClose, stream, actualMimeType]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle cancel button click
  const handleCancel = () => {
    // Release the media stream before closing
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
      });
      setStream(null);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-green-700 text-white p-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">📹 Record Your Story</h2>
              <p className="text-green-100">Capture precious memories in your own voice and image</p>
            </div>
            <button
              onClick={handleCancel}
              className="text-white hover:text-green-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Mode Selector */}
        <div className="p-6">
          <div className="flex gap-3 mb-4 justify-center">
            <button
              onClick={() => switchMode('video')}
              className={`px-6 py-3 rounded-lg border-2 transition-all ${
                currentMode === 'video'
                  ? 'bg-green-700 text-white border-green-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'
              }`}
            >
              <Video className="inline mr-2" size={20} />
              Video
            </button>
            <button
              onClick={() => switchMode('audio')}
              className={`px-6 py-3 rounded-lg border-2 transition-all ${
                currentMode === 'audio'
                  ? 'bg-green-700 text-white border-green-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'
              }`}
            >
              <Mic className="inline mr-2" size={20} />
              Audio Only
            </button>
          </div>

          {/* Device Selectors */}
          {(availableMicrophones.length > 0 || availableCameras.length > 0) && (
            <div className="flex flex-col items-center gap-3 mb-6">
              {/* Camera Selector - only in video mode */}
              {currentMode === 'video' && availableCameras.length > 0 && (
                <div className="flex items-center justify-center gap-2">
                  <Video size={16} className="text-gray-500" />
                  <label htmlFor="camera-select" className="text-sm text-gray-600">
                    Camera:
                  </label>
                  <div className="relative">
                    <select
                      id="camera-select"
                      value={selectedCameraId}
                      onChange={(e) => handleCameraChange(e.target.value)}
                      disabled={isRecording}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[200px]"
                    >
                      {availableCameras.map((cam) => (
                        <option key={cam.deviceId} value={cam.deviceId}>
                          {cam.label || `Camera ${cam.deviceId.slice(0, 8)}...`}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Microphone Selector */}
              {availableMicrophones.length > 0 && (
                <div className="flex items-center justify-center gap-2">
                  <Mic size={16} className="text-gray-500" />
                  <label htmlFor="mic-select" className="text-sm text-gray-600">
                    Microphone:
                  </label>
                  <div className="relative">
                    <select
                      id="mic-select"
                      value={selectedMicrophoneId}
                      onChange={(e) => handleMicrophoneChange(e.target.value)}
                      disabled={isRecording}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed min-w-[200px]"
                    >
                      {availableMicrophones.map((mic) => (
                        <option key={mic.deviceId} value={mic.deviceId}>
                          {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}...`}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Capture Area */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex flex-col items-center gap-6">
              {/* Video Preview */}
              {currentMode === 'video' && (
                <div className="relative">
                  <video
                    ref={videoRef}
                    className="w-full max-w-lg h-72 bg-black rounded-lg"
                    autoPlay
                    muted
                    playsInline
                  />
                  {isRecording && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm animate-pulse">
                      🔴 RECORDING
                    </div>
                  )}
                </div>
              )}

              {/* Audio Visualizer */}
              {currentMode === 'audio' && (
                <div className="w-full max-w-lg h-32 bg-black rounded-lg flex items-center justify-center relative">
                  <div className="flex items-end gap-1 h-20">
                    {Array.from({ length: 30 }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 bg-green-400 rounded-full transition-all duration-100 ${
                          isRecording ? 'animate-pulse' : ''
                        }`}
                        style={{
                          height: isRecording ? `${Math.random() * 40 + 10}px` : '10px'
                        }}
                      />
                    ))}
                  </div>
                  {isRecording && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm animate-pulse">
                      🔴 RECORDING
                    </div>
                  )}
                </div>
              )}

              {/* Audio element for playback */}
              <audio ref={audioRef} className="hidden" />

              {/* Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isLoading}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                    isRecording
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                      : 'bg-orange-500 hover:bg-orange-600'
                  } text-white disabled:opacity-50`}
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin" size={24} />
                  ) : isRecording ? (
                    <Square size={24} />
                  ) : (
                    <div className="w-4 h-4 bg-white rounded-sm" />
                  )}
                </button>

                {recordedChunks.length > 0 && (
                  <button
                    onClick={playRecording}
                    disabled={isPlaying}
                    className="w-12 h-12 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                )}
              </div>

              {/* Timer */}
              <div className="text-lg font-mono text-green-700">
                {formatTime(recordingTime)}
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveMedia}
              disabled={recordedChunks.length === 0}
              className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Save Media
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaCapture; 